// Duplicates already sitting in the directory.
//
// This exists because nobody goes looking. The four in FERN GROUP sat in the
// directory for weeks — counting twice in headcount, appearing twice in the
// directory — and were found in a screenshot, not by anyone using the product.
//
// Read-only on purpose. It names the pairs and says which record was probably
// never reached; deciding what to do with a person's record is not something to
// offer behind a one-click button next to nineteen other rows.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CopyCheck, ChevronRight, UserX, UserCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { reasonLabel } from '@/features/shared/components/DuplicatePersonWarning';
import { listPossibleDuplicates, type DuplicateGroup } from '@/lib/hrApi';

export const PossibleDuplicatesCard: React.FC<{ employeesBase: string }> = ({ employeesBase }) => {
  const { t } = useTranslation(['employees']);
  const [groups, setGroups] = useState<DuplicateGroup[] | null>(null);

  useEffect(() => { listPossibleDuplicates().then(setGroups).catch(() => setGroups([])); }, []);

  // Render nothing at all when the directory is clean. A card that permanently
  // says "0 duplicates" is a card people learn to scroll past, which is exactly
  // when it matters that they notice it turning non-zero.
  if (!groups?.length) return null;

  return (
    <Card className="border-warning-strong/25">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CopyCheck className="w-4 h-4 text-warning-strong" />
          {t('dup.title', { defaultValue: 'Possible duplicate people' })}
          <span className="text-xs font-normal text-muted-foreground">
            {t('dup.count', { defaultValue: '{{n}} to review', n: groups.length })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          {t('dup.hint', {
            defaultValue:
              'Usually a mistyped email: the person was invited, never reached, and added again. Open each record to decide which to keep.',
          })}
        </p>
        {groups.slice(0, 20).map((g) => (
          <div key={g.records.map((r) => r.employeeId).join('|')}
               className="rounded-lg border border-border p-3 space-y-2">
            <p className="text-xs text-muted-foreground">{reasonLabel(t, g.reason)}</p>
            {g.records.map((r) => (
              <Link
                key={r.employeeId}
                to={`${employeesBase}/${r.employeeId}`}
                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm hover:underline"
              >
                {r.hasLogin
                  ? <UserCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  : <UserX className="w-3.5 h-3.5 text-warning-strong shrink-0" />}
                <span className="font-medium text-foreground">{r.name || r.email}</span>
                <span className="text-muted-foreground break-all">{r.email}</span>
                <span className="text-xs text-muted-foreground">· {r.status}</span>
                {/* The record nobody ever signed into is almost always the typo,
                    so it is called out rather than left to be inferred. */}
                {!r.hasLogin && (
                  <span className="text-xs text-warning-strong">
                    · {t('dup.never_signed_in', { defaultValue: 'never signed in' })}
                  </span>
                )}
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0" />
              </Link>
            ))}
          </div>
        ))}
        {groups.length > 20 && (
          <p className="text-xs text-muted-foreground">
            {t('dup.more', { defaultValue: 'and {{n}} more', n: groups.length - 20 })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default PossibleDuplicatesCard;
